import React, { useEffect, useState, useRef, useCallback } from "react";
import { Container, Card, Form, Row, Col, Button, Badge, Alert, Spinner, InputGroup } from "react-bootstrap";
import { api, getStaticData, httpPost, isValidDate, parseEMailPhone } from "../pureFunctions";
import { header, host } from "../globalVariables";

import { useConfig } from "../ConfigContext";

import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import { de } from "date-fns/locale/de";
registerLocale("de", de);
import {
   generateUebergabeprotokollPDF,
   openPdfInNewTab,
   downloadPdf,
   savePdfToDevice,
   isIOS,
   pdfBytesToBase64,
   downscaleToJpeg,
   readBlobBytes,
} from "./pdfProtocol";
import { sendMail } from "../networkFunctions";
import { buildHandoverEmailHtml, buildHandoverEmailSubject } from "./emailTemplate";
import { useM365Context } from "../m365Context";
import { saveFormState, loadFormState } from "./localFormCache";

// ---------------------------------------------------------------------------
// Konfiguration
// ---------------------------------------------------------------------------
const API_BASE_URL = "/api";
const PDF_ACCENT_COLOR = "#95c11f";
const PDF_LOGO_URL = null;

const ZAEHLER_ARTEN = ["Strom", "Gas", "Wasser (kalt)", "Wasser (warm)", "Fernwärme", "Heizung", "Sonstige"];
const initalBeteiligte = { name: "", email: "", id: "" };
const initProgress = { send: false, uploaded: false, downloaded: false };

// ---------------------------------------------------------------------------
// Hauptkomponente
// ---------------------------------------------------------------------------
export default function UebergabeFormular() {
   const config = useConfig();
   const [sendResult, setSendResult] = useState(null); // { variant, message, pdfBytes, filename }
   const [showTest, setShowTest] = useState(false);
   const [diagnose, setDiagnose] = useState(null);
   const [estateId, setEstateId] = useState(null);
   const [eskStatus, setEskStatus] = useState();
   const [jsonObject, setJsonObject] = useState();
   const [usersData, setUsersData] = useState();

   const [loading, setLoading] = useState(false);
   const [loadError, setLoadError] = useState(null);
   const [submitting, setSubmitting] = useState(false);
   const [spFolderId, setSpFolderId] = useState("");
   const [progress, setProgress] = useState(initProgress);
   const { getSharepointData, getSharepointFile, uploadFileToSharepoint } = useM365Context();

   // 1) Objektdaten
   const [objekt, setObjekt] = useState({
      strasse: "",
      hausnummer: "",
      plz: "",
      ort: "",
      uebergabeDatum: "",
   });

   // 2) Beteiligte
   const [beteiligte, setBeteiligte] = useState({
      owner: [initalBeteiligte],
      buyer: [initalBeteiligte],
      makler: [{ name: "Baur Immobilien GmbH", email: "info@baur-immobilien.de" }],
   });

   // 3) Zählerstände
   const [zaehler, setZaehler] = useState([
      { id: uid(), art: "Strom", nummer: "", stand: "", foto: null },
      { id: uid(), art: "Wasser (kalt)", nummer: "", stand: "", foto: null },
   ]);

   // 4) Schlüssel & Übergabegegenstände
   const [schluessel, setSchluessel] = useState([
      { id: uid(), bezeichnung: "Haustürschlüssel", anzahl: 1 },
      { id: uid(), bezeichnung: "Wohnungsschlüssel", anzahl: 1 },
      { id: uid(), bezeichnung: "Briefkastenschlüssel", anzahl: 1 },
   ]);

   // 5) Zustand, Mängel & Notizen
   const [maengel, setMaengel] = useState("");
   const [notizen, setNotizen] = useState("");

   // 6) Fotos (allgemein)
   const [fotos, setFotos] = useState([]);
   const [fotosPreview, setFotosPreview] = useState([]);
   const [fotosProcessing, setFotosProcessing] = useState(false);

   useEffect(() => {
      if (!fotos) return;
      const urls = fotos.map((foto) => URL.createObjectURL(foto));
      setFotosPreview(urls);
      return () => urls.forEach((u) => URL.revokeObjectURL(u)); // alte URLs freigeben
   }, [fotos]);

   // 7) Unterschriften - ein Eintrag je Person, Index passend zu beteiligte.owner/buyer/makler
   const [signatures, setSignatures] = useState({
      owner: [],
      buyer: [],
      makler: [],
   });

   const updateSignature = (rolle, index, value) => {
      setSignatures((prev) => {
         const list = [...(prev[rolle] || [])];
         list[index] = value;
         return { ...prev, [rolle]: list };
      });
   };

   // -------------------------------------------------------------------------
   // Objektdaten anhand estateId von der API laden
   // -------------------------------------------------------------------------

   async function loadData() {
      const params = new URLSearchParams(window.location.search);
      let url_estateId = params.get("estateId");
      setEstateId(url_estateId);

      if (!url_estateId) return;

      setLoading(true);
      setLoadError(null);

      let [newestatekacheln, cleanUserData, newfieldConfiguration] = await getStaticData();

      setUsersData(cleanUserData);

      let estateBasicData = await api
         .post(
            "/api/getEstateBasicData",
            [
               {
                  id: url_estateId,
                  datarequest: config.uebergabeFormular.estateOOFields,
               },
            ],
            header,
         )
         .catch((err) => {
            console.log(err);

            setLoadError({
               error: "onOffice",
               message:
                  "<strong>Fehler beim Laden der onOffice-Objektdaten</strong> <br/> <br/>" +
                  JSON.stringify(err.response.data, null, 1),
            });
         })
         .finally(() => setLoading(false));
      console.log(estateBasicData);

      let ooJSONObject;
      try {
         ooJSONObject = JSON.parse(estateBasicData.data[config.estate.JSONFeldname] || {});
         setJsonObject(ooJSONObject);
         delete estateBasicData.data[config.estate.JSONFeldname];
      } catch (error) {
         setLoadError({
            error: "onOffice",
            message: "<h2>Fehler im JSON Object</h2>" + estateBasicData.data[config.estate.JSONFeldname],
         });
      }
      try {
         setEskStatus(JSON.parse(estateBasicData.data[config.estate.status] || {}));
         delete estateBasicData.data[config.estate.status];
      } catch (error) {
         setLoadError({
            error: "onOffice",
            message: "<h2>Fehler im JSON Object</h2>" + estateBasicData.data[config.estate.status],
         });
      }
      setSpFolderId(estateBasicData?.data[config.estate.sharepointFolderId]);
      let oldUbergabeData;
      try {
         oldUbergabeData = JSON.parse(estateBasicData.data[config.estateSonstige.uebergabeFormular] || null);
         delete estateBasicData.data[config.estateSonstige.uebergabeFormular];
      } catch (error) {
         setLoadError({
            error: "onOffice",
            message: "<h2>Fehler im JSON Object</h2>" + estateBasicData.data[config.estateSonstige.uebergabeFormular],
         });
      }

      console.log(estateBasicData);

      if (oldUbergabeData) {
         setObjekt(oldUbergabeData.objekt);
         setBeteiligte(oldUbergabeData.beteiligte);
      } else {
         setObjekt((prev) => ({ ...prev, ...estateBasicData.data, ...ooJSONObject }));

         const respEstateBuyer = await loadRelationData(
            url_estateId,
            "buyer",
            config.uebergabeFormular.addressOOFields,
         );
         const respEstateOwner = await loadRelationData(
            url_estateId,
            "owner",
            config.uebergabeFormular.addressOOFields,
         );
         setBeteiligte((prev) => ({
            ...prev,
            makler: [
               {
                  name:
                     cleanUserData?.userObject[estateBasicData.data.benutzer].Nachname +
                     " " +
                     cleanUserData?.userObject[estateBasicData.data.benutzer].Vorname,
                  email: "info@baur-immobilien.de",
               },
            ],
         }));
      }
   }
   useEffect(() => {
      loadData();
   }, []);

   // -------------------------------------------------------------------------
   // Lokaler Zwischenspeicher (IndexedDB): Formularfelder + Fotos überleben einen Reload.
   // -------------------------------------------------------------------------
   const restoredRef = useRef(false);

   // estateId ist beim ersten Aufruf noch null und wird erst asynchron aus der URL gesetzt (loadData) -
   // der Effect läuft daher erneut, sobald sie bekannt ist, und lädt dann unter dem richtigen Key.
   // Fotos werden beim Wiederherstellen in frische In-Memory-Dateien umgewandelt, weil Safari Blobs
   // aus IndexedDB zwar anzeigen, aber oft nicht mehr lesen kann (-> fehlende Fotos in der PDF).
   useEffect(() => {
      let cancelled = false;
      restoredRef.current = false;
      loadFormState(estateId)
         .then(async (saved) => {
            if (!saved || cancelled) return;

            const restoredFotos = [];
            for (const f of saved.fotos ?? []) {
               restoredFotos.push(await rehydrateFile(f));
            }
            let restoredZaehler = null;
            if (saved.zaehler) {
               restoredZaehler = [];
               for (const z of saved.zaehler) {
                  restoredZaehler.push(z.foto ? { ...z, foto: await rehydrateFile(z.foto) } : z);
               }
            }
            if (cancelled) return;

            setObjekt((prev) => saved.objekt ?? prev);
            setBeteiligte((prev) => saved.beteiligte ?? prev);
            setZaehler((prev) => restoredZaehler ?? prev);
            setSchluessel((prev) => saved.schluessel ?? prev);
            setMaengel(saved.maengel ?? "");
            setNotizen(saved.notizen ?? "");
            setFotos(restoredFotos);
            setSignatures((prev) => saved.signatures ?? prev);
         })
         .catch((err) => console.warn("Lokaler Zwischenspeicher konnte nicht gelesen werden:", err))
         .finally(() => {
            if (!cancelled) restoredRef.current = true;
         });
      return () => {
         cancelled = true;
      };
   }, [estateId]);

   useEffect(() => {
      if (!restoredRef.current) return;
      const timer = setTimeout(() => {
         saveFormState(
            { objekt, beteiligte, zaehler, schluessel, maengel, notizen, fotos, signatures },
            estateId,
         ).catch((err) => console.warn("Formular konnte nicht lokal zwischengespeichert werden:", err));
      }, 600);
      return () => clearTimeout(timer);
   }, [estateId, objekt, beteiligte, zaehler, schluessel, maengel, notizen, fotos, signatures]);

   async function saveData(isDone) {
      let updatedEskStatus;
      if (isDone) {
         updatedEskStatus = structuredClone(eskStatus);
         updatedEskStatus["kachel_status_" + "kaufuebergabe"] = 4;
         updatedEskStatus["kachelabschluss_von_" + "kaufuebergabe"] = usersData.userObject[objekt.benutzer].Name;
      }
      await api
         .post(
            "/api/updateEstateDatenblatt",
            [
               {
                  estateId,
                  oOObjectValue: {
                     [config.estateSonstige.uebergabeFormular]: JSON.stringify({ objekt, beteiligte }, null, 1),
                     ...(isDone
                        ? {
                             [config.estate.status]: JSON.stringify(updatedEskStatus, null, 1),
                             [config.estate.JSONFeldname]: JSON.stringify(
                                {
                                   ...jsonObject,
                                   kaueg_pers: objekt.benutzer,
                                   kaueg_Dat: objekt.uebergabeDatum,
                                },
                                null,
                                1,
                             ),
                          }
                        : {}),
                  },
               },
            ],
            header,
         )
         .then((data) => {
            console.log(data);
         });
   }

   async function loadRelationData(estateId, relationType, fieldList) {
      const respRelationIds = (
         await httpPost(
            host + "/api/EstateRelation",
            { estateId: estateId, relationType: relationType },
            setLoading,
            setLoadError,
         )
      )?.data?.[estateId];

      console.log(respRelationIds);

      const relationData = await Promise.all(
         respRelationIds.map(async (element, index) => {
            const respRelationData =
               (
                  await httpPost(
                     host + "/api/postSpecificAddress",
                     {
                        id: element,
                        data: ["KdNr", "Vorname", "Name", "email", "ID"],
                     },
                     setLoading,
                     setLoadError,
                  )
               )?.data ?? {};

            const emails = parseEMailPhone(respRelationData, "email");
            return {
               email: emails.all[0],
               id: respRelationData.ID,
               name: respRelationData.Name + " " + respRelationData.Vorname,
            };
         }),
      );
      console.log(relationData);

      setBeteiligte((prev) => ({ ...prev, [relationType]: relationData }));
   }

   // -------------------------------------------------------------------------
   // Handler
   // -------------------------------------------------------------------------
   const updateObjekt = (field, value) => setObjekt((prev) => ({ ...prev, [field]: value }));

   function addBeteiligte(rolle) {
      let oldBeteiligte = structuredClone(beteiligte);
      oldBeteiligte[rolle].push({ ...initalBeteiligte });
      setBeteiligte(oldBeteiligte);
   }
   function removeBeteiligte(rolle, index) {
      let oldBeteiligte = structuredClone(beteiligte);
      oldBeteiligte[rolle].splice(index, 1);
      setBeteiligte(oldBeteiligte);
   }

   const updateBeteiligte = (rolle, field, value, index) => {
      let oldBeteiligte = structuredClone(beteiligte);
      if (oldBeteiligte[rolle][index]) {
         oldBeteiligte[rolle][index][field] = value;
      } else {
         oldBeteiligte[rolle].push({ ...initalBeteiligte, [field]: value });
      }
      setBeteiligte(oldBeteiligte);
   };

   const addZaehler = () =>
      setZaehler((prev) => [...prev, { id: uid(), art: ZAEHLER_ARTEN[0], nummer: "", stand: "", foto: null }]);

   const updateZaehler = (id, field, value) =>
      setZaehler((prev) => prev.map((z) => (z.id === id ? { ...z, [field]: value } : z)));

   const removeZaehler = (id) => setZaehler((prev) => prev.filter((z) => z.id !== id));

   const addSchluessel = () => setSchluessel((prev) => [...prev, { id: uid(), bezeichnung: "", anzahl: 1 }]);

   const updateSchluessel = (id, field, value) =>
      setSchluessel((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));

   const removeSchluessel = (id) => setSchluessel((prev) => prev.filter((s) => s.id !== id));

   const handleZaehlerFoto = async (id, file) => {
      if (!file) return;
      try {
         updateZaehler(id, "foto", await normalizePhoto(file));
      } catch (err) {
         console.warn(err);
         alert(`Zählerfoto konnte nicht verarbeitet werden: ${err?.message || err}`);
      }
   };

   const handleFotosChange = async (fileList) => {
      const files = Array.from(fileList || []); // synchron kopieren, bevor irgendetwas awaited wird
      if (!files.length) return;
      setFotosProcessing(true);
      const prepared = [];
      for (const f of files) {
         try {
            prepared.push(await normalizePhoto(f));
         } catch (err) {
            console.warn(err);
            alert(`Foto "${f.name}" konnte nicht verarbeitet werden: ${err?.message || err}`);
         }
      }
      setFotos((prev) => [...prev, ...prepared]);
      setFotosProcessing(false);
   };

   const removeFoto = (index) => setFotos((prev) => prev.filter((_, i) => i !== index));

   // Testet jeden Lese-Weg einzeln - Ausgabe kann vom iPad per Screenshot geschickt werden.
   async function runDiagnose() {
      const alle = [
         ...fotos.map((f, i) => ({ name: `Foto ${i + 1}`, file: f })),
         ...zaehler.filter((z) => z.foto).map((z) => ({ name: `Zähler ${z.art}`, file: z.foto })),
      ];
      const test = async (fn) => {
         try {
            await fn();
            return "ok";
         } catch (e) {
            return "FEHLER: " + (e?.name || "") + " " + (e?.message || e);
         }
      };
      const zeilen = [
         "UserAgent: " + navigator.userAgent,
         "createImageBitmap vorhanden: " + (typeof createImageBitmap === "function"),
         "Blob.arrayBuffer vorhanden: " + (typeof Blob.prototype.arrayBuffer === "function"),
         "Anzahl Fotos: " + alle.length,
         "",
      ];
      for (const { name, file } of alle) {
         zeilen.push(`${name}: Typ=${file?.constructor?.name} mime="${file?.type}" size=${file?.size}`);
         zeilen.push(
            "  createImageBitmap: " +
               (await test(async () => {
                  if (typeof createImageBitmap !== "function") throw new Error("nicht vorhanden");
                  const bmp = await createImageBitmap(file);
                  bmp.close?.();
               })),
         );
         zeilen.push("  arrayBuffer: " + (await test(() => file.arrayBuffer())));
         zeilen.push("  FileReader: " + (await test(() => readBlobBytes(file))));
         zeilen.push("  img + canvas: " + (await test(() => downscaleToJpeg(file))));
      }
      setDiagnose(zeilen.join("\n"));
   }

   const buildProtocolData = useCallback(
      () => ({
         objekt,
         beteiligte,
         // "Sonstige" wird für die PDF durch den frei eingegebenen Text ersetzt.
         zaehler: zaehler.map((z) => ({
            ...z,
            art: z.art === "Sonstige" ? z.artSonstige?.trim() || "Sonstige" : z.art,
         })),
         schluessel,
         maengel,
         notizen,
         fotos,
         signatures,
      }),
      [objekt, beteiligte, zaehler, schluessel, maengel, notizen, fotos, signatures],
   );

   async function handleSubmit(action, color, logo_url) {
      setSubmitting(true);
      // Außerhalb von try deklariert, damit der Notfall-Download im catch darauf zugreifen kann.
      let pdfBytes = null;
      const filename = `Uebergabeformular_${objekt.strasse}_${objekt.hausnummer}_${objekt.ort}.pdf`.replace(
         /\s+/g,
         "_",
      );
      try {
         pdfBytes = await generateUebergabeprotokollPDF(
            buildProtocolData(),
            {
               accentColor: color,
               logoUrl: logo_url,
            },
            config.uebergabeFormular.estateForm,
         );

         if (action === "preview") {
            openPdfInNewTab(pdfBytes);
            return;
         }
         if (action === "download") {
            downloadPdf(pdfBytes, filename);
            return;
         }

         // Das PDF wird clientseitig erzeugt; das Backend übernimmt nur noch den Mailversand.
         const propertyLabel = [
            `${objekt.strasse} ${objekt.hausnummer}`.trim(),
            [objekt.plz, objekt.ort].filter(Boolean).join(" "),
         ]
            .filter(Boolean)
            .join(", ");
         const mailSubject = buildHandoverEmailSubject(propertyLabel);
         const mailContent = buildHandoverEmailHtml({ propertyLabel, filename, accentColor: PDF_ACCENT_COLOR });
         const receiver = [...beteiligte.owner, ...beteiligte.buyer];
         await sendMail(
            estateId,
            mailSubject,
            mailContent,
            receiver,
            (resp) => {
               console.log(resp);
            },
            { name: filename, content: pdfBytesToBase64(pdfBytes) },
         )
            .then((resp) => {
               setProgress((prev) => ({ ...prev, send: "success" }));
            })
            .catch((err) => {
               setProgress((prev) => ({ ...prev, send: "error" }));
            });

         await saveData(true);
         const finalBlob = new Blob([pdfBytes], { type: "application/pdf" });

         // Show the result in the page first, so it is visible no matter what the download does.
         await uploadFileToSharepoint(finalBlob, config.ms365.driveId, spFolderId, `Uebergabeprotokoll.pdf`)
            .then((resp) => {
               setProgress((prev) => ({ ...prev, uploaded: "success" }));
            })
            .catch((e) => {
               setProgress((prev) => ({ ...prev, uploaded: "error" }));
               alert(`Fehler: ${e.name}: ${e.message}\nGröße: ${finalBlob?.size}, Typ: ${finalBlob?.type}`);
               throw `Datei konnte nicht zum Sharepoint hochgeladen werden\n${e}`;
            });

         if (action === "send") {
            // Windows/desktop: normal download. iOS: octet-stream, so the page stays open.
            downloadPdf(pdfBytes, filename, { forceDownload: isIOS() })
         }

        /*  setSendResult({
            variant: "success",
            message: "PDF wurde erstellt, per E-Mail versendet und in SharePoint abgelegt.",
            pdfBytes,
            filename,
         }); */
      } catch (error) {
         console.log(error);
         setSendResult({
            variant: "danger",
            message: `Fehler: ${error?.message || error}`,
            pdfBytes, // may be null if PDF generation itself failed
            filename,
         });
         try {
            if (action === "send" && pdfBytes) {
               downloadPdf(pdfBytes, filename, { forceDownload: isIOS() });
            }
         } catch (error2) {
            console.log(error2);
         }
      } finally {
         setSubmitting(false);
      }
   }

   function hasMissingField() {
      const pflichtfelder = config.uebergabeFormular.estateForm.filter((item) => !item.optional);
      let missing = [{ key: "uebergabeDatum" }, ...pflichtfelder].some((item) => !objekt[item.key]);
      return missing;
   }

   // -------------------------------------------------------------------------
   // Render
   // -------------------------------------------------------------------------
   return (
      <div className="d-flex justify-content-center">
         {/* <h5>Übergabeprotokoll</h5> */}
         <Container className="py-4 mx-0" style={{ maxWidth: 1180 }}>
            {loading && (
               <div className="d-flex align-items-center gap-2 mb-3 text-muted">
                  <Spinner animation="border" size="sm" /> Objektdaten werden geladen…
               </div>
            )}
            {loadError && (
               <Alert variant="danger">Objektdaten konnten nicht geladen werden: {JSON.stringify(loadError)}</Alert>
            )}

            {/* 01 Objektdaten */}
            <SectionCard step="01" title="Objektdaten" subtitle="Adresse und Eckdaten der Übergabe">
               <Row>
                  {config.uebergabeFormular.estateForm.map((item) => {
                     return (
                        <Col key={item.key} xs={12} md={6}>
                           <Form.Group className="mb-3">
                              <Form.Label>
                                 {item.name}
                                 {!item.optional && <span className="text-danger">*</span>}
                              </Form.Label>
                              <Form.Control
                                 className={item.optional ? "" : objekt[item.key] ? "" : "empty-field"}
                                 name={item.key}
                                 value={objekt?.[item.key] ?? ""}
                                 onChange={(e) => updateObjekt(e.target.name, e.target.value)}
                              />
                           </Form.Group>
                        </Col>
                     );
                  })}
               </Row>

               <Row>
                  <Col xs={12} md={12}>
                     <div className="col" style={{ position: "relative" }}>
                        <Form.Group controlId={"id_uebergabeDatum"} className="mb-2 mt-2">
                           <Form.Label>Datum und Uhrzeit der Übergabe</Form.Label>

                           <DatePicker
                              selected={isValidDate(objekt.uebergabeDatum) && new Date(objekt.uebergabeDatum)}
                              showTimeSelect={true}
                              timeIntervals={5}
                              onChange={(value) => {
                                 updateObjekt("uebergabeDatum", value);
                              }}
                              className={"bootstrap-datepicker " + (objekt.uebergabeDatum ? "" : " empty-field")}
                              calendarClassName="custom-date-calendar"
                              timeFormat="HH:mm"
                              dateFormat={"dd.MM.yyyy, HH:mm"}
                              locale="de"
                           />
                        </Form.Group>
                        <button
                           title="heute und jetzt als Datum einstellen"
                           className="now-button "
                           onClick={(e) => {
                              e.preventDefault();
                              updateObjekt("uebergabeDatum", new Date());
                           }}
                        >
                           {"˅"}
                        </button>{" "}
                     </div>
                  </Col>
               </Row>
            </SectionCard>

            {/* 02 Beteiligte */}
            <SectionCard step="02" title="Beteiligte" subtitle="E-Mail-Adressen werden für den Versand verwendet">
               <Row className="">
                  {beteiligte.owner.map((beteiligter, index) => {
                     return (
                        <BeteiligterSpalte
                           key={`owner-${index}`}
                           label="Verkäufer"
                           data={beteiligter}
                           onChange={(field, value) => updateBeteiligte("owner", field, value, index)}
                           emailPlaceholder="name@beispiel.de"
                           removeBeteiligte={() => {
                              removeBeteiligte("owner", index);
                           }}
                        />
                     );
                  })}
                  <AddBeteiligterSpalte label="Verkäufer" addBeteiligte={addBeteiligte} relationType={"owner"} />
               </Row>
               <Row className="">
                  {beteiligte.buyer.map((beteiligter, index) => {
                     return (
                        <BeteiligterSpalte
                           key={`buyer-${index}`}
                           label="Käufer"
                           data={beteiligter}
                           onChange={(field, value) => updateBeteiligte("buyer", field, value, index)}
                           emailPlaceholder="name@beispiel.de"
                           removeBeteiligte={() => {
                              removeBeteiligte("buyer", index);
                           }}
                        />
                     );
                  })}
                  <AddBeteiligterSpalte label="Käufer" addBeteiligte={addBeteiligte} relationType={"buyer"} />
               </Row>
               <Row>
                  <BeteiligterSpalte
                     label="Makler"
                     userlist={usersData?.userList}
                     data={beteiligte.makler[0]}
                     onChange={(field, value) => updateBeteiligte("makler", field, value, 0)}
                     emailPlaceholder="info@baur-immobilien.de"
                  />
               </Row>
            </SectionCard>
            <Row className="mb-4">
               <Col md={12}>
                  <Button
                     variant="secondary"
                     className="w-100"
                     disabled={submitting}
                     onClick={() => {
                        saveData();
                     }}
                  >
                     Daten speichern
                  </Button>
               </Col>
            </Row>
            {/* 03 Zählerstände */}
            <SectionCard step="03" title="Zählerstände" subtitle="Stand ablesen und ein Foto des Zählers aufnehmen">
               {zaehler.map((z) => (
                  <Card key={z.id} className="mb-3 p-3 bg-light">
                     <Row className="align-items-end mb-2">
                        <Col md={4}>
                           <Form.Label>Zählerart</Form.Label>
                           <Form.Select value={z.art} onChange={(e) => updateZaehler(z.id, "art", e.target.value)}>
                              {ZAEHLER_ARTEN.map((a) => (
                                 <option key={a} value={a}>
                                    {a}
                                 </option>
                              ))}
                           </Form.Select>
                           {z.art === "Sonstige" && (
                              <Form.Control
                                 className="mt-2"
                                 placeholder="Bezeichnung der Zählerart"
                                 value={z.artSonstige || ""}
                                 onChange={(e) => updateZaehler(z.id, "artSonstige", e.target.value)}
                              />
                           )}
                        </Col>
                        <Col md={4}>
                           <Form.Label>Zählernummer</Form.Label>
                           <Form.Control
                              placeholder="Nr."
                              value={z.nummer}
                              onChange={(e) => updateZaehler(z.id, "nummer", e.target.value)}
                           />
                        </Col>
                        <Col md={3}>
                           <Form.Label>Zählerstand</Form.Label>
                           <Form.Control
                              placeholder="Stand"
                              value={z.stand}
                              onChange={(e) => updateZaehler(z.id, "stand", e.target.value)}
                           />
                        </Col>
                        <Col md={1} className="text-end">
                           <Button variant="outline-secondary" onClick={() => removeZaehler(z.id)}>
                              ×
                           </Button>
                        </Col>
                     </Row>
                     <PhotoDropzone
                        file={z.foto}
                        onChange={(file) => handleZaehlerFoto(z.id, file)}
                        label="Foto des Zählers aufnehmen"
                     />
                  </Card>
               ))}
               <Button variant="outline-dark" onClick={addZaehler}>
                  + Zähler hinzufügen
               </Button>
            </SectionCard>

            {/* 04 Schlüssel & Übergabegegenstände */}
            <SectionCard
               step="04"
               title="Schlüssel & Übergabegegenstände"
               subtitle="Anzahl der übergebenen Schlüssel und Gegenstände"
            >
               {schluessel.map((s) => (
                  <Row key={s.id} className="mb-2 align-items-center">
                     <Col md={8}>
                        <Form.Control
                           placeholder="Bezeichnung"
                           value={s.bezeichnung}
                           onChange={(e) => updateSchluessel(s.id, "bezeichnung", e.target.value)}
                        />
                     </Col>
                     <Col md={2}>
                        <Form.Control
                           type="number"
                           min={0}
                           value={s.anzahl}
                           onChange={(e) => updateSchluessel(s.id, "anzahl", e.target.value)}
                        />
                     </Col>
                     <Col md={2} className="text-end">
                        <Button variant="outline-secondary" onClick={() => removeSchluessel(s.id)}>
                           ×
                        </Button>
                     </Col>
                  </Row>
               ))}
               <Button variant="outline-dark" onClick={addSchluessel}>
                  + Eintrag hinzufügen
               </Button>
            </SectionCard>

            {/* 05 Zustand, Mängel & Notizen */}
            <SectionCard
               step="05"
               title="Zustand, Mängel & Notizen"
               subtitle="Freitext für Vereinbarungen und Beobachtungen"
            >
               <Form.Group className="mb-3">
                  <Form.Label>Festgestellte Mängel</Form.Label>
                  <Form.Control
                     as="textarea"
                     rows={3}
                     placeholder="z. B. Kratzer Parkett Wohnzimmer, fehlende Abdeckung Steckdose Küche ..."
                     value={maengel}
                     onChange={(e) => setMaengel(e.target.value)}
                  />
               </Form.Group>
               <Form.Group>
                  <Form.Label>Vereinbarungen / sonstige Notizen</Form.Label>
                  <Form.Control
                     as="textarea"
                     rows={3}
                     placeholder="z. B. Restmüll wird bis Freitag entfernt, Nachsendeauftrag eingerichtet ..."
                     value={notizen}
                     onChange={(e) => setNotizen(e.target.value)}
                  />
               </Form.Group>
            </SectionCard>

            {/* 06 Fotos */}
            <SectionCard step="06" title="Fotos" subtitle="Allgemeine Fotos zum Zustand des Objekts">
               <div className="d-flex flex-wrap gap-1">
                  <PhotoDropzone
                     multiple
                     label={fotosProcessing ? "Fotos werden verarbeitet…" : "Foto aufnehmen"}
                     file={null}
                     onChange={(_, fileList) => handleFotosChange(fileList)}
                     compact
                  />
                  {fotosPreview.map((f, i) => {
                     return (
                        <div
                           key={f}
                           className="flex-col"
                           style={{
                              border: "solid 1px #0002",
                              padding: 5,
                              borderRadius: 5,
                              maxWidth: "49%",
                              position: "relative",
                           }}
                        >
                           <img style={{ maxWidth: "100%" }} src={f} />
                           <div className="d-flex align-items-center justify-content-between mt-1 gap-1">
                              <Badge bg="dark" className="align-self-center">
                                 {fotos[i]?.name}
                              </Badge>
                              <Button variant="outline-secondary" size="sm" onClick={() => removeFoto(i)}>
                                 ×
                              </Button>
                           </div>
                        </div>
                     );
                  })}
               </div>
            </SectionCard>

            {/* 07 Unterschriften */}
            <SectionCard step="07" title="Unterschriften" subtitle="">
               {beteiligte.owner.map((beteiligter, index) => {
                  const label =
                     beteiligte.owner.length > 1
                        ? `Verkäufer${beteiligter.name ? " – " + beteiligter.name : ` ${index + 1}`}`
                        : "Verkäufer";
                  return (
                     <SignaturePad
                        key={`owner-${index}`}
                        label={label}
                        value={signatures.owner?.[index] || null}
                        onChange={(v) => updateSignature("owner", index, v)}
                     />
                  );
               })}
               {beteiligte.buyer.map((beteiligter, index) => {
                  const label =
                     beteiligte.buyer.length > 1
                        ? `Käufer${beteiligter.name ? " – " + beteiligter.name : ` ${index + 1}`}`
                        : "Käufer";
                  return (
                     <SignaturePad
                        key={`buyer-${index}`}
                        label={label}
                        value={signatures.buyer?.[index] || null}
                        onChange={(v) => updateSignature("buyer", index, v)}
                     />
                  );
               })}

               <div className="d-flex justify-content-between align-items-baseline mb-2">
                  <div className="text-uppercase fw-semibold small text-warning-emphasis">Makler</div>
                  <div className="text-muted">{beteiligte.makler[0]?.name}</div>
               </div>
               <SignaturePad
                  label=""
                  value={signatures.makler?.[0] || null}
                  onChange={(v) => updateSignature("makler", 0, v)}
               />
            </SectionCard>
            {hasMissingField() && <Alert variant="warning">Objektdaten sind nicht vollständig ausgefüllt!</Alert>}

            {sendResult && (
               <Alert variant={sendResult.variant} dismissible onClose={() => setSendResult(null)}>
                  <div>{sendResult.message}</div>
                  {sendResult.pdfBytes && (
                     <Button
                        variant="outline-dark"
                        size="sm"
                        className="mt-2"
                        onClick={() => savePdfToDevice(sendResult.pdfBytes, sendResult.filename)}
                     >
                        PDF auf dem Gerät speichern
                     </Button>
                  )}
               </Alert>
            )}
            {(progress.send || progress.uploaded) && (
               <Alert variant="light">
                  {progress.send === "success" && <Alert variant="success"> ✅ Mails wurden versendet!</Alert>}
                  {progress.send === "error" && (
                     <Alert variant="danger">
                       ❌ Fehler! Mails konnten nicht versendet werden! Bitte PDF herunterladen und später versuchen. ❌
                     </Alert>
                  )}
                  {progress.uploaded === "success" && (
                     <Alert variant="success">✅ PDF wurde erfolgreich zum Sharepoint hochgeladen!</Alert>
                  )}
                  {progress.uploaded === "error" && (
                     <Alert variant="danger">
                       ❌ Fehler! PDF konnte nicht zu Sharepoint hochgeladen werden! Bitte PDF herunterladen und später
                        manuell hochladen. ❌
                     </Alert>
                  )}
               </Alert>
            )}
            <Row className="g-2">
               <Col md={4}>
                  <Button
                     variant="outline-dark"
                     className="w-100"
                     disabled={submitting || fotosProcessing}
                     onClick={() => {
                        handleSubmit("preview", config?.colorScheme?.signature, host + "/images/logo_light.png");
                     }}
                  >
                     PDF ansehen
                  </Button>
               </Col>
               <Col md={4}>
                  <Button
                     variant="outline-dark"
                     className="w-100"
                     disabled={submitting || fotosProcessing}
                     onClick={() =>
                        handleSubmit("download", config.colorScheme.signature, host + "/images/logo_light.png")
                     }
                  >
                     PDF herunterladen
                  </Button>
               </Col>
               <Col md={4}>
                  <Button
                     style={{ background: config.colorScheme.signature, border: "solid 2px #333" }}
                     className="w-100 text-white"
                     disabled={submitting || fotosProcessing || hasMissingField()}
                     onClick={() => handleSubmit("send", config.colorScheme.signature, host + "/images/logo_light.png")}
                  >
                     {submitting ? <Spinner animation="border" size="sm" /> : "PDF erstellen & per E-Mail senden"}
                  </Button>
               </Col>
            </Row>
            <Button
               variant="outline-secondary"
               onClick={() => {
                  setShowTest((prev) => {
                     if (!prev) runDiagnose();
                     return !prev;
                  });
               }}
               className="mt-5 w-100"
            >
               Test (einfach ignorieren)
            </Button>
            {showTest && <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>{diagnose ?? "Diagnose läuft…"}</pre>}
         </Container>
      </div>
   );
}

// ---------------------------------------------------------------------------
// Weitere Unterkomponenten
// ---------------------------------------------------------------------------
function SectionCard({ step, title, subtitle, children }) {
   const config = useConfig();
   return (
      <Card className="mb-4 shadow-sm overflow-hidden">
         <Card.Header className="bg-white d-flex align-items-center gap-3 py-3">
            <Badge
               bg="light"
               text="dark"
               className="fs-6 px-3 py-2"
               style={{ border: "solid 3px " + config.colorScheme.signature }}
            >
               {step}
            </Badge>
            <div>
               <div className="fw-bold fs-5">{title}</div>
               {subtitle && <div className="text-muted small">{subtitle}</div>}
            </div>
         </Card.Header>
         <Card.Body>{children}</Card.Body>
      </Card>
   );
}

function BeteiligterSpalte({ label, data, onChange, emailPlaceholder, removeBeteiligte, userlist }) {
   return (
      <Col md={4} className="mb-3">
         <Card className="h-100 p-3 bg-light">
            <div className="text-uppercase fw-semibold small mb-3">{label}</div>
            {removeBeteiligte && (
               <Button
                  variant="outline-secondary"
                  style={{ position: "absolute", right: 0, top: 0, margin: 10 }}
                  onClick={() => removeBeteiligte()}
               >
                  ×
               </Button>
            )}

            {label === "Makler" ? (
               <Form.Group className="mb-3">
                  <Form.Select onChange={(e) => onChange("name", e.target.value)} value={data?.name ?? ""}>
                     <option value=""></option>
                     {userlist &&
                        userlist.map((user) => {
                           return (
                              <option key={"selectuser_" + user.Name} value={user.Nachname + " " + user.Vorname}>
                                 {user.Nachname + " " + user.Vorname}
                              </option>
                           );
                        })}
                  </Form.Select>
               </Form.Group>
            ) : (
               <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                     placeholder="Vor- und Nachname"
                     value={data?.name ?? ""}
                     onChange={(e) => onChange("name", e.target.value)}
                  />
               </Form.Group>
            )}
            <Form.Group>
               <Form.Label>E-Mail</Form.Label>
               <Form.Control
                  type="email"
                  placeholder={emailPlaceholder}
                  value={data?.email ?? ""}
                  onChange={(e) => onChange("email", e.target.value)}
               />
            </Form.Group>
         </Card>
      </Col>
   );
}

function AddBeteiligterSpalte({ label, addBeteiligte, relationType }) {
   return (
      <Col md={4} className="mb-3">
         <Card className="h-100 p-3 bg-light">
            <div className="text-uppercase fw-semibold small mb-3">{label}</div>

            <Button
               className="h-100 w-100 "
               variant="outline-dark"
               onClick={() => {
                  addBeteiligte(relationType);
               }}
            >
               hinzufügen
            </Button>
         </Card>
      </Col>
   );
}

function PhotoDropzone({ file, onChange, label, multiple, compact }) {
   const inputRef = useRef(null);
   const [preview, setPreview] = useState(null);

   const handleFiles = (e) => {
      // Sofort in ein Array kopieren, dann Input zurücksetzen (gleiche Datei erneut wählbar).
      const files = Array.from(e.target.files || []);
      e.target.value = "";
      if (!files.length) return;
      if (multiple) {
         onChange(null, files);
      } else {
         onChange(files[0]);
      }
   };

   useEffect(() => {
      if (!file) {
         setPreview(null);
         return;
      }
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url); // alte URL freigeben
   }, [file]);

   return (
      <div
         role="button"
         onClick={() => inputRef.current?.click()}
         className="border border-dashed rounded text-center text-muted d-flex  flex-col align-items-center justify-content-center"
         style={{
            borderStyle: "dashed",
            minHeight: compact ? 130 : 150,
            width: compact ? 150 : "100%",
            cursor: "pointer",
            background: "#fff",
         }}
      >
         <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple={multiple}
            className="d-none"
            onChange={handleFiles}
         />
         {preview && <img style={{ maxWidth: "60%", margin: "5px 0" }} src={preview} />}
         <div className="fw-semibold small mt-1">{file ? file.name : label}</div>
      </div>
   );
}

function uid() {
   return Math.random().toString(36).slice(2, 10);
}

// ---------------------------------------------------------------------------
// Foto-Hilfsfunktionen (iOS-sicher)
// ---------------------------------------------------------------------------

// Komprimiert ein Foto direkt bei der Auswahl und erzeugt eine eigene, im Speicher liegende JPEG-Datei.
// Damit hängt das Foto nicht mehr an der temporären iOS-Datei und lässt sich zuverlässig in IndexedDB speichern.
async function normalizePhoto(file) {
   const bytes = await downscaleToJpeg(file);
   const name = (file.name || "foto").replace(/\.[^.]+$/, "") + ".jpg";
   return new File([bytes], name, { type: "image/jpeg" });
}

// Holt ein aus IndexedDB wiederhergestelltes Foto als frische In-Memory-Datei zurück.
// Erst verlustfrei per FileReader, falls das scheitert über den <img>-Weg (wie die Vorschau).
async function rehydrateFile(file) {
   if (!file || !(file instanceof Blob)) return file;
   try {
      const bytes = await readBlobBytes(file);
      return new File([bytes], file.name || "foto.jpg", { type: file.type || "image/jpeg" });
   } catch (err) {
      console.warn("Foto nicht direkt lesbar, versuche Umweg über <img>:", err);
      try {
         return await normalizePhoto(file);
      } catch (err2) {
         console.warn("Foto konnte nicht wiederhergestellt werden:", err2);
         return file; // Original behalten, damit nichts verloren geht
      }
   }
}

// ---------------------------------------------------------------------------
// Unterkomponente: einfaches Unterschriften-Feld (Canvas, Finger/Pencil/Maus)
// ---------------------------------------------------------------------------
const SIGNATURE_WIDTH = 700;
const SIGNATURE_HEIGHT = 180;
// Interne Zeichenfläche an die Gerätepixeldichte koppeln (z. B. Retina-iPad), damit Linien nicht
// verpixelt aussehen - Deckel bei 2x, damit die exportierte PNG-Größe nicht unnötig aufgebläht wird.
const SIGNATURE_DPR = Math.min(typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1, 2);

function SignaturePad({ label, value, onChange }) {
   const canvasRef = useRef(null);
   const drawingRef = useRef(false);

   // Zeichnet eine wiederhergestellte Unterschrift (z. B. aus dem lokalen Zwischenspeicher) auf das
   // Canvas - sonst ist der State zwar gefüllt (und landet im PDF), das Canvas bleibt aber leer.
   useEffect(() => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!value) {
         ctx.clearRect(0, 0, canvas.width, canvas.height);
         return;
      }
      const img = new Image();
      img.onload = () => {
         ctx.clearRect(0, 0, canvas.width, canvas.height);
         ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.src = value;
   }, [value]);

   const getCoords = (e) => {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const point = e.touches ? e.touches[0] : e;
      // Canvas wird per CSS responsiv skaliert (width: 100%) – Koordinaten müssen daher
      // vom angezeigten CSS-Pixelraster auf das interne Canvas-Koordinatensystem umgerechnet werden.
      return {
         x: ((point.clientX - rect.left) * canvas.width) / rect.width,
         y: ((point.clientY - rect.top) * canvas.height) / rect.height,
      };
   };

   const start = (e) => {
      drawingRef.current = true;
      const ctx = canvasRef.current.getContext("2d");
      const { x, y } = getCoords(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
   };

   const move = (e) => {
      if (!drawingRef.current) return;
      e.preventDefault();
      const ctx = canvasRef.current.getContext("2d");
      const { x, y } = getCoords(e);
      ctx.lineWidth = 2 * SIGNATURE_DPR;
      ctx.lineCap = "round";
      ctx.strokeStyle = "#1c1c1c";
      ctx.lineTo(x, y);
      ctx.stroke();
   };

   const end = () => {
      if (!drawingRef.current) return;
      drawingRef.current = false;
      onChange(canvasRef.current.toDataURL("image/png"));
   };

   const clear = () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      onChange(null);
   };

   return (
      <div className="mb-4">
         <div className="text-uppercase fw-semibold small text-warning-emphasis mb-2">{label}</div>
         <Card className="p-2">
            <div className="d-flex justify-content-end mb-2">
               <Button variant="outline-secondary" size="sm" onClick={clear}>
                  Löschen
               </Button>
            </div>
            <canvas
               ref={canvasRef}
               width={SIGNATURE_WIDTH * SIGNATURE_DPR}
               height={SIGNATURE_HEIGHT * SIGNATURE_DPR}
               style={{
                  width: "100%",
                  maxWidth: SIGNATURE_WIDTH,
                  height: "auto",
                  border: "1px solid #dee2e6",
                  borderRadius: 4,
                  touchAction: "none",
               }}
               onMouseDown={start}
               onMouseMove={move}
               onMouseUp={end}
               onMouseLeave={end}
               onTouchStart={start}
               onTouchMove={move}
               onTouchEnd={end}
            />
            <div className="text-muted small mt-1">Hier unterschreiben</div>
         </Card>
      </div>
   );
}
